import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CatalogSuggestionMatchConfidence } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { Cover } from '../../../music24/components/primitives/Cover';
import { CatalogSuggestion } from '../../../../redux/api/fymAPI.types';
import { ConnectOption } from '../ConnectOption';
import { MergePickStep } from '../merge/MergePickStep';
import { catalogArtworkSrc } from '../catalogArtwork';
import { formatCatalogDay } from '../catalogDates';
import { claimWayMessages } from './claimWayMessages';
import { claimMergeOptions } from './claimWayRules';
import { ClaimWayState } from './useClaimWay';
import { ClaimWay } from './useClaimRoute';
import { MatchTag } from './MatchTag';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the claim way; each part uses its own slice of it */
import styles from './ClaimWay.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ClaimWayChoiceProps {
  suggestion: CatalogSuggestion;
  claimWay: ClaimWayState;
}

/**
 * The first part of the claim dialog of a suggestion (design catalog-merge c2, c3): a new release,
 * or a merge into a song of the catalog. The matched row shows as the pick; "Pick another song"
 * opens the picker of every row.
 */
const ClaimWayChoiceComponent: React.FC<ClaimWayChoiceProps> = ({ suggestion, claimWay }) => {
  const intl = useIntl();
  const match = suggestion.matchesCatalogItem ?? null;
  const { way, intoItem, items, setWay, setInto } = claimWay;
  const [isPicking, setPicking] = useState(false);
  const options = useMemo(() => claimMergeOptions(items, match?.id), [items, match?.id]);
  const onWay = useCallback((value: ClaimWay) => setWay(value), [setWay]);
  const pickAnother = useCallback(() => setPicking(true), []);
  const showChosen = way === 'merge' && !isPicking && intoItem !== undefined && intoItem.uuid === match?.id;

  return (
    <div className="d-flex flex-column gap-3">
      <div className={styles.question}>
        {intl.formatMessage(claimWayMessages.question, { title: suggestion.title })}
      </div>
      <div className={styles.ways} role="radiogroup">
        <ConnectOption
          value="new"
          selected={way}
          onSelect={onWay}
          title={intl.formatMessage(claimWayMessages.wayNew)}
          description={
            match === null
              ? intl.formatMessage(claimWayMessages.wayNewHelp)
              : intl.formatMessage(claimWayMessages.wayNewNextTo, { title: match.title })
          }
        />
        <ConnectOption
          value="merge"
          selected={way}
          onSelect={onWay}
          title={intl.formatMessage(claimWayMessages.wayMerge)}
          description={intl.formatMessage(claimWayMessages.wayMergeHelp)}
        />
      </div>
      {showChosen && intoItem !== undefined && (
        <>
          <span className={styles.heading}>{intl.formatMessage(claimWayMessages.matchesHeading)}</span>
          <div className={styles.chosen} data-testid="claim-way-chosen">
            <Cover src={catalogArtworkSrc(intoItem, 44)} label={intoItem.title} size={44} />
            <div className={styles.chosenInfo}>
              <div className="fw-bold">{intoItem.title}</div>
              {intoItem.releaseDate !== null && (
                <div className="small text-muted">{formatCatalogDay(intl, intoItem.releaseDate)}</div>
              )}
            </div>
            <MatchTag isSure={match?.confidence === CatalogSuggestionMatchConfidence.sure} />
            <SMLButton onClick={pickAnother}>{intl.formatMessage(claimWayMessages.pickAnother)}</SMLButton>
          </div>
        </>
      )}
      {way === 'merge' && !showChosen && (
        <MergePickStep candidates={options} isLoading={false} selectedUuid={intoItem?.uuid} onSelect={setInto} />
      )}
    </div>
  );
};

export const ClaimWayChoice = TypedMemo(ClaimWayChoiceComponent);
